import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { ActionType, ObjectTypes } from '@fi-shared/types';
import { IHistoryAdditionalData, IHistoryModel } from '@fi-app/history/history';
import { AppTextService } from '@fi-shared/app-text';

@Component({
    selector: 'fi-history-list-action-column',
    template: `
        <span class="CcmListItemTextOverflow CcmListItemName" [attr.title]="getDisplayValue()">
            {{ getDisplayValue() }}
            @if (isSuperAdminIconVisible()) {
                <span class="fas fa-user-secret super-admin-icon"></span>
            }
        </span>
    `,
    styleUrls: ['./history-list-action-column.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-history-list-action-column' },
    standalone: false,
})
export class HistoryListActionColumnComponent {
    @Input() historyItem: IHistoryModel;

    constructor(private appTextService: AppTextService) {}

    getDisplayValue() {
        return this.getActionTypeText(
            this.historyItem.ActionType,
            this.historyItem.ObjectType,
            this.historyItem.AdditionalData,
        );
    }

    private getActionTypeText(actionType: ActionType, objectType: ObjectTypes, additionalData: IHistoryAdditionalData) {
        if (!additionalData) {
            additionalData = {};
        }
        if (actionType === ActionType.Create) return this.appTextService.get('history.created');
        if (actionType === ActionType.Update) return this.appTextService.get('history.edited');
        if (actionType === ActionType.Delete) return this.appTextService.get('history.deleted');
        if (actionType === ActionType.ChangeOwner) return this.appTextService.get('history.change_owner');

        if (actionType === ActionType.Published) return this.appTextService.get('common.published');
        if (actionType === ActionType.Unpublished) return this.appTextService.get('common.unpublished');

        if (actionType === ActionType.MoveToTrash) {
            return objectType !== ObjectTypes.Task
                ? this.appTextService.get('history.moved_to_trash')
                : this.appTextService.get('history.archived');
        }
        if (actionType === ActionType.Restore) return this.appTextService.get('history.restored');
        if (actionType === ActionType.ShareExistingContent) {
            const actionTypeText = this.appTextService.get('history.share_existing_content');

            if (!additionalData.AffectedItemsCount) return actionTypeText;

            const details = this.appTextService
                .get('history.share_existing_content_details')
                .replace('{0}', <any>additionalData.AffectedItemsCount)
                .replace('{1}', additionalData.SignText ?? '');
            return actionTypeText + details;
        }
        if (actionType === ActionType.DeleteResponses) return this.appTextService.get('history.delete_responses');
        if (actionType === ActionType.CreateAccessRequest) return this.appTextService.get('history.sent_request');
        if (actionType === ActionType.AcceptAccessRequest) return this.appTextService.get('history.approved_request');
        if (actionType === ActionType.RejectAccessRequest) return this.appTextService.get('history.denied_request');
        if (actionType === ActionType.MarkAsCompleted) return this.appTextService.get('history.mark_as_completed');
        if (actionType === ActionType.MarkAsUncompleted) return this.appTextService.get('history.mark_as_uncompleted');

        const businessUnitName = additionalData.BusinessUnitName || '-';
        if (actionType === ActionType.CheckListSetNotApplicable)
            return this.appTextService.get('history.checklist_set_not_applicable', { '{0}': businessUnitName });
        if (actionType === ActionType.CheckListSetApplicable)
            return this.appTextService.get('history.checklist_set_applicable', { '{0}': businessUnitName });

        if (actionType === ActionType.CheckPointSetNotApplicable)
            return this.appTextService.get('history.checkpoint_set_not_applicable', { '{0}': businessUnitName });
        if (actionType === ActionType.CheckPointSetApplicable)
            return this.appTextService.get('history.checkpoint_set_applicable', { '{0}': businessUnitName });

        if (actionType === ActionType.CheckListSetGlobalMobileOn)
            return this.appTextService.get('history.checklist_set_global_mobile_on');
        if (actionType === ActionType.CheckListSetGlobalMobileOff)
            return this.appTextService.get('history.checklist_set_global_mobile_off');

        if (actionType === ActionType.Pinned) return this.appTextService.get('post.pinned');
        if (actionType === ActionType.RemovedPin) return this.appTextService.get('post.removed_pin');

        if (actionType === ActionType.OpenSupportSession) return this.appTextService.get('history.logged_in_as');

        if (actionType === ActionType.SuperAdminViewAccess) return this.appTextService.get('common.view');

        if (actionType === ActionType.ChainformationSupportMessage)
            return this.appTextService.get('history.chainformation_support_message');

        if (actionType === ActionType.InternalSupportMessage)
            return this.appTextService.get('history.internal_support_message');

        if (actionType === ActionType.TransferOwnership) {
            const actionTypeText = this.appTextService.get('history.transfer_ownership', {
                '{from_user_name}': additionalData.FromUserName,
                '{to_user_name}': additionalData.ToUserName,
            });
            return actionTypeText;
        }

        return '';
    }

    isSuperAdminIconVisible() {
        return this.historyItem.ActionType === ActionType.SuperAdminViewAccess;
    }
}
